import { matchesCurrentMoveOutRequest } from '../../../utils/portvilleMoveOutRequest';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../../hooks/useAuth';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { db, storage } from '../../../firebase';
import ClientLayout from '../../../components/layout/ClientLayout';
import { MoveOutFormData } from '../../../types/moveOutRequest';

interface ResidencyInfo {
  residencyId: string;
  createdAt?: Date;
  roomNumber: string;
  roomId: string;
  contractorInfo: {
    name: string;
    phoneNumber: string;
    organization: string;
  };
  occupancyPeriod: {
    startDate: Date;
    endDate: Date;
  };
  status: 'occupied' | 'contracted';
}

const MoveOutRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { loading: emailVerificationLoading } = useEmailVerification();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [residencyInfo, setResidencyInfo] = useState<ResidencyInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canApplyMoveOut, setCanApplyMoveOut] = useState(false);
  const [hasExistingRequest, setHasExistingRequest] = useState(false);
  
  const [formData, setFormData] = useState<MoveOutFormData>({
    moveOutReason: '',
    desiredMoveOutDate: '',
    idCopyFile: null,
    bankbookCopyFile: null,
    proofDocumentFile: null
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !authLoading && !emailVerificationLoading) {
      loadResidencyInfo();
    }
  }, [user, authLoading, emailVerificationLoading]);

  const loadResidencyInfo = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      setResidencyInfo(null);
      setCanApplyMoveOut(false);
      setHasExistingRequest(false);
      
      // 1. 새로운 데이터 구조에서 "현재 유효한" 입주 기록 확인
      // - completed/terminated 등 과거 계약을 대상으로 퇴거신청이 생성되지 않도록 필터링
      // - 최신 순으로 가져온 뒤 active 우선 선택
      const residenciesRef = collection(db, 'portville_residencies');
      const residencyQuery = query(residenciesRef, where('residentUid', '==', user.uid));
      const residencySnapshot = await getDocs(residencyQuery);

      const getMillis = (value: any): number => {
        try {
          if (!value) return 0;
          if (typeof value.toMillis === 'function') return value.toMillis();
          if (typeof value.toDate === 'function') return value.toDate().getTime();
          return 0;
        } catch {
          return 0;
        }
      };

      const activeDocs = residencySnapshot.docs
        .filter((d) => d.data().status === 'active')
        .sort((a, b) => getMillis(b.data().createdAt) - getMillis(a.data().createdAt));

      const contractingSignedDocs = residencySnapshot.docs
        .filter((d) => {
          const data = d.data();
          return data.status === 'contracting' && !!data.contractSignedAt;
        })
        .sort((a, b) => getMillis(b.data().createdAt) - getMillis(a.data().createdAt));

      const eligibleResidencyDoc = activeDocs[0] || contractingSignedDocs[0];

      if (eligibleResidencyDoc) {
        const residencyData = eligibleResidencyDoc.data();
        const residencyId = eligibleResidencyDoc.id;
        
        // 계약이 완료되지 않은 경우 리다이렉트
        if (!residencyData.contractSignedAt) {
          navigate('/portville');
          return;
        }

        if (!residencyData.occupancyPeriod?.endDate) {
          setError('계약 기간 정보가 없어 퇴거 신청을 진행할 수 없습니다. 관리자에게 문의해주세요.');
          return;
        }
        
        const endDate = residencyData.occupancyPeriod?.endDate?.toDate() || new Date();
        const today = new Date();
        const daysUntilEnd = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        setResidencyInfo({
          residencyId,
          createdAt: residencyData.createdAt?.toDate(),
          roomNumber: residencyData.roomNumber,
          roomId: residencyData.roomId,
          contractorInfo: {
            name: residencyData.residentInfo?.name || '',
            phoneNumber: residencyData.residentInfo?.phoneNumber || '',
            organization: residencyData.residentInfo?.organization || ''
          },
          occupancyPeriod: {
            startDate: residencyData.occupancyPeriod?.startDate?.toDate() || new Date(),
            endDate: endDate
          },
          status: residencyData.status === 'active' ? 'occupied' : 'contracted'
        });
        
        // 계약 종료일 하루 전까지 퇴거 신청 가능
        setCanApplyMoveOut(daysUntilEnd >= 1);
        
        // 기본 퇴거 희망일 설정 (계약 종료일 하루 전)
        if (daysUntilEnd >= 1) {
          const defaultMoveOutDate = new Date(endDate);
          defaultMoveOutDate.setDate(defaultMoveOutDate.getDate() - 1);
          setFormData(prev => ({
            ...prev,
            desiredMoveOutDate: defaultMoveOutDate.toISOString().split('T')[0]
          }));
        }
        
        return;
      }
      
      // 2. 기존 데이터 구조 호환성 확인
      const lotteryRef = collection(db, 'portville_lottery_history');
      const lotteryQuery = query(lotteryRef, where('uid', '==', user.uid));
      const lotterySnapshot = await getDocs(lotteryQuery);
      
      if (lotterySnapshot.empty) {
        navigate('/portville');
        return;
      }
      
      const lotteryData = lotterySnapshot.docs[0].data();
      if (lotteryData.status !== 'contracted') {
        navigate('/portville');
        return;
      }
      
      const roomDoc = await getDoc(doc(db, 'portville_rooms', lotteryData.roomId));
      if (!roomDoc.exists()) {
        throw new Error('호실 정보를 찾을 수 없습니다.');
      }
      
      const roomData = roomDoc.data();
      const endDate = roomData.occupancyPeriod?.endDate?.toDate() || new Date();
      const today = new Date();
      const daysUntilEnd = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      setResidencyInfo({
        residencyId: '',
        roomNumber: roomData.roomNumber,
        roomId: lotteryData.roomId,
        contractorInfo: {
          name: roomData.currentResident?.name || '',
          phoneNumber: roomData.currentResident?.phoneNumber || '',
          organization: roomData.currentResident?.organization || ''
        },
        occupancyPeriod: {
          startDate: roomData.occupancyPeriod?.startDate?.toDate() || new Date(),
          endDate: endDate
        },
        status: roomData.status === 'occupied' ? 'occupied' : 'contracted'
      });
      
      setCanApplyMoveOut(daysUntilEnd >= 1);
      
      if (daysUntilEnd >= 1) {
        const defaultMoveOutDate = new Date(endDate);
        defaultMoveOutDate.setDate(defaultMoveOutDate.getDate() - 1);
        setFormData(prev => ({
          ...prev,
          desiredMoveOutDate: defaultMoveOutDate.toISOString().split('T')[0]
        }));
      }
      
    } catch (error) {
      console.error('Error loading residency info:', error);
      setError('입주 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!residencyInfo) return;

    const check = async () => {
      if (!user) return;

      try {
        const moveOutRequestsRef = collection(db, 'portville_move_out_requests');
        const moveOutRequestQuery = query(
          moveOutRequestsRef,
          where('residentUid', '==', user.uid),
          where('roomId', '==', residencyInfo.roomId),
          where('status', '==', 'pending')
        );
        const moveOutRequestSnapshot = await getDocs(moveOutRequestQuery);
        setHasExistingRequest(moveOutRequestSnapshot.docs.some(snapshot =>
          residencyInfo.residencyId
            ? matchesCurrentMoveOutRequest(snapshot.data() as any, {
                roomId: residencyInfo.roomId, residencyId: residencyInfo.residencyId,
                residentUid: user.uid, createdAt: residencyInfo.createdAt
              })
            : !snapshot.data().residencyId
        ));
      } catch (error) {
        console.error('Error checking existing move out request:', error);
      }
    };

    check();
  }, [user, residencyInfo]);

  const uploadFile = async (file: File, path: string): Promise<string> => {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `${path}/${fileName}`);
    
    const snapshot = await uploadBytes(storageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const validateForm = (): string | null => {
    if (!formData.moveOutReason) {
      return '퇴거 사유를 선택해주세요.';
    }
    
    if (!formData.desiredMoveOutDate) {
      return '퇴거 희망일을 선택해주세요.';
    }
    
    const selectedDate = new Date(formData.desiredMoveOutDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    if (selectedDate < tomorrow) {
      return '퇴거 희망일은 최소 내일 이후로 선택해주세요.';
    }
    
    if (residencyInfo && selectedDate >= residencyInfo.occupancyPeriod.endDate) {
      return '퇴거 희망일은 계약 종료일 이전으로 선택해주세요.';
    }
    
    if (!formData.idCopyFile) {
      return '신분증 사본을 첨부해주세요.';
    }
    
    if (!formData.bankbookCopyFile) {
      return '환급 통장 사본을 첨부해주세요.';
    }
    
    if (formData.moveOutReason === 'personnel_change' && !formData.proofDocumentFile) {
      return '인사변경 사유 선택시 증빙서류를 첨부해주세요.';
    }
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !residencyInfo) return;
    
    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }
    
    try {
      setSubmitting(true);
      
      // 파일 업로드
      const idCopyUrl = await uploadFile(formData.idCopyFile!, 'move_out_requests/id_copies');
      const bankbookCopyUrl = await uploadFile(formData.bankbookCopyFile!, 'move_out_requests/bankbook_copies');
      
      let proofDocumentUrl: string | undefined;
      if (formData.proofDocumentFile) {
        proofDocumentUrl = await uploadFile(formData.proofDocumentFile, 'move_out_requests/proof_documents');
      }
      
      // 퇴거 신청 데이터 저장
      const moveOutRequestData = {
        residentUid: user.uid,
        ...(residencyInfo.residencyId ? { residencyId: residencyInfo.residencyId } : {}),
        roomId: residencyInfo.roomId,
        roomNumber: residencyInfo.roomNumber,
        residentInfo: {
          name: residencyInfo.contractorInfo.name,
          organization: residencyInfo.contractorInfo.organization,
          phoneNumber: residencyInfo.contractorInfo.phoneNumber
        },
        moveOutReason: formData.moveOutReason,
        desiredMoveOutDate: Timestamp.fromDate(new Date(formData.desiredMoveOutDate)),
        attachments: {
          idCopy: idCopyUrl,
          bankbookCopy: bankbookCopyUrl,
          ...(proofDocumentUrl && { proofDocument: proofDocumentUrl })
        },
        status: 'pending',
        appliedAt: Timestamp.now(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      await addDoc(collection(db, 'portville_move_out_requests'), moveOutRequestData);
      
      alert('퇴거 신청이 완료되었습니다. 검토 후 연락드리겠습니다.');
      navigate('/portville/residency');
      
    } catch (error) {
      console.error('Error submitting move out request:', error);
      alert('퇴거 신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (field: keyof MoveOutFormData, file: File | null) => {
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    if (!residencyInfo) return '';
    const maxDate = new Date(residencyInfo.occupancyPeriod.endDate);
    maxDate.setDate(maxDate.getDate() - 1);
    return maxDate.toISOString().split('T')[0];
  };

  if (authLoading || emailVerificationLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <div className="flex flex-col min-h-screen bg-white">
          <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
            <div className="w-full max-w-[750px] px-4 sm:px-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
                <p className="text-red-800 text-center">{error}</p>
              </div>
              <button
                onClick={() => navigate('/portville')}
                className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
              >
                포트빌 메인으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!residencyInfo) {
    return null;
  }

  if (hasExistingRequest) {
    return (
      <ClientLayout>
        <div className="flex flex-col min-h-screen bg-white">
          <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
            <div className="w-full max-w-[750px] px-4 sm:px-6">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-800 font-['Pretendard'] leading-9 mb-4">
                포트빌 퇴거 신청
              </h1>
              <div className="w-full h-px bg-gray-200 mb-8"></div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-bold text-blue-900">퇴거 신청 완료</h3>
                </div>
                <div className="text-blue-800">
                  <p className="mb-2">이미 퇴거 신청을 완료하셨습니다.</p>
                  <p className="text-sm">현재 상태는 입주 현황 페이지에서 확인하실 수 있습니다.</p>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => navigate('/portville/residency')}
                  className="w-full h-14 sm:h-16 bg-blue-600 text-white rounded-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                >
                  입주 현황으로 돌아가기
                </button>
                
                <button
                  onClick={() => navigate('/portville')}
                  className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                >
                  포트빌 메인으로 돌아가기
                </button>
              </div>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!canApplyMoveOut) {
    return (
      <ClientLayout>
        <div className="flex flex-col min-h-screen bg-white">
          <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
            <div className="w-full max-w-[750px] px-4 sm:px-6">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-800 font-['Pretendard'] leading-9 mb-4">
                포트빌 퇴거 신청
              </h1>
              <div className="w-full h-px bg-gray-200 mb-8"></div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h3 className="text-lg font-bold text-red-900">퇴거 신청 불가</h3>
                </div>
                <div className="text-red-800">
                  <p className="mb-2">계약 종료일 당일 및 이후에는 퇴거 신청을 할 수 없습니다.</p>
                  <p className="text-sm">계약 종료일: {residencyInfo.occupancyPeriod.endDate.toLocaleDateString('ko-KR')}</p>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => navigate('/portville/residency')}
                  className="w-full h-14 sm:h-16 bg-blue-600 text-white rounded-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                >
                  입주 현황으로 돌아가기
                </button>
                
                <button
                  onClick={() => navigate('/portville')}
                  className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                >
                  포트빌 메인으로 돌아가기
                </button>
              </div>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        {/* 본문 컨테이너 - 중앙 정렬 */}
        <div className="w-[760px] mx-auto relative pt-8">
          {/* 헤더 섹션 */}
          <div className="mt-8">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              포트빌 퇴거 신청
            </div>
            <div className="mt-4 text-slate-700 text-xl font-normal font-['Pretendard'] leading-9">
              퇴거 신청을 위한 정보를 입력해주세요
            </div>
          </div>

          {/* 구분선 */}
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

          {/* 안내 메시지 */}
          <div className="mt-8">
            <div className="bg-blue-50 p-8 rounded border border-black/5">
              <div className="flex items-center mb-4">
                <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-slate-800 text-xl font-bold font-['Pretendard'] leading-9">퇴거 신청 안내</h3>
              </div>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>퇴거 신청은 퇴거 예정일 최소 10일 전에 신청하시기를 권장합니다.</li>
                <li>신청된 서류는 시설관리센터의 검토 후 퇴거 절차 및 일정을 안내드릴 예정입니다.</li>
                <li>퇴거 점검 및 보증금 정산 등의 절차가 진행됩니다.</li>
                <li>필수 첨부서류를 모두 업로드해주세요. 증빙서류 미비시 퇴거 신청이 승인되지 않을 수 있습니다.</li>
              </ul>
            </div>
          </div>

          {/* 입주자 정보 */}
          <div className="mt-8">
            <div className="text-slate-800 text-xl font-bold font-['Pretendard'] leading-9">
              현재 입주 정보
            </div>
            <div className="mt-4 w-full bg-gray-50 rounded border border-black/5 p-8">
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-gray-800 text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                    호실
                  </div>
                  <div className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 flex items-center px-4">
                    <div className="text-gray-700 text-base font-medium font-['Pretendard'] leading-relaxed">
                      {residencyInfo.roomNumber}호
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="text-gray-800 text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                    입주자
                  </div>
                  <div className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 flex items-center px-4">
                    <div className="text-gray-700 text-base font-medium font-['Pretendard'] leading-relaxed">
                      {residencyInfo.contractorInfo.name}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-gray-800 text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                    소속
                  </div>
                  <div className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 flex items-center px-4">
                    <div className="text-gray-700 text-base font-medium font-['Pretendard'] leading-relaxed">
                      {residencyInfo.contractorInfo.organization}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-gray-800 text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                    계약 종료일
                  </div>
                  <div className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 flex items-center px-4">
                    <div className="text-gray-700 text-base font-medium font-['Pretendard'] leading-relaxed">
                      {residencyInfo.occupancyPeriod.endDate.toLocaleDateString('ko-KR')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 퇴거 신청 폼 */}
          <form onSubmit={handleSubmit} className="mt-8 space-y-8">
            {/* 퇴거 사유 */}
            <div>
              <div className="text-slate-800 text-xl font-bold font-['Pretendard'] leading-9">
                퇴거 사유 <span className="text-red-500">*</span>
              </div>
              <div className="mt-4 w-full bg-gray-50 rounded border border-black/5 p-8">
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="moveOutReason"
                      value="personnel_change"
                      checked={formData.moveOutReason === 'personnel_change'}
                      onChange={(e) => setFormData(prev => ({ ...prev, moveOutReason: e.target.value as any }))}
                      className="mr-4 w-4 h-4 rounded border-gray-300 focus:ring-0 checked:bg-gray-500 checked:border-gray-500"
                    />
                    <span className="text-gray-800 text-base font-medium font-['Pretendard'] leading-relaxed">
                      인사변경 (퇴직, 이직, 휴직 등)
                    </span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="moveOutReason"
                      value="personal_reason"
                      checked={formData.moveOutReason === 'personal_reason'}
                      onChange={(e) => setFormData(prev => ({ ...prev, moveOutReason: e.target.value as any }))}
                      className="mr-4 w-4 h-4 rounded border-gray-300 focus:ring-0 checked:bg-gray-500 checked:border-gray-500"
                    />
                    <span className="text-gray-800 text-base font-medium font-['Pretendard'] leading-relaxed">
                      개인사유
                    </span>
                  </label>
                </div>
              </div>
              
              {/* 개인사유 선택시 안내 메시지 */}
              {formData.moveOutReason === 'personal_reason' && (
                <div className="mt-4 w-full opacity-90 bg-yellow-50 rounded-sm outline outline-1 outline-yellow-300">
                  <div className="p-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <p className="text-yellow-800 text-base font-normal font-['Pretendard'] leading-relaxed">
                        개인적인 사유로 인해 퇴거할 경우, 조기퇴거로 인한 비용 27만원이 발생합니다.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 퇴거 희망일 */}
            <div>
              <div className="text-slate-800 text-xl font-bold font-['Pretendard'] leading-9">
                퇴거 희망일 <span className="text-red-500">*</span>
              </div>
              <div className="mt-4 w-full bg-gray-50 rounded border border-black/5 p-8">
                <div>
                  <div className="text-gray-800 text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                    퇴거 희망일
                  </div>
                  <input
                    type="date"
                    value={formData.desiredMoveOutDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, desiredMoveOutDate: e.target.value }))}
                    min={getMinDate()}
                    max={getMaxDate()}
                    className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-gray-700 text-base font-medium font-['Pretendard'] leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="mt-2 text-[#42506e] text-sm font-normal font-['Pretendard'] leading-relaxed">
                    선택 가능 기간: {getMinDate()} ~ {getMaxDate()}
                  </p>
                </div>
              </div>
            </div>

            {/* 첨부서류 */}
            <div>
              <div className="text-slate-800 text-xl font-bold font-['Pretendard'] leading-9">
                필요 서류 업로드
              </div>
              <div className="mt-4 bg-gray-50 p-8 rounded border border-black/5">
                <h2 className="text-slate-800 text-xl font-bold font-['Pretendard'] leading-9 mb-6">
                  필요 서류 안내
                </h2>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>신분증 사본 (주민등록증, 운전면허증 등)</li>
                  <li>환급 통장 사본</li>
                  {formData.moveOutReason === 'personnel_change' && (
                    <li>증빙서류 (퇴직증명서, 발령장 등)</li>
                  )}
                </ul>
              </div>

              <div className="mt-8 space-y-8">
                {/* 신분증 사본 */}
                <div>
                  <h3 className="text-slate-800 text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                    신분증 사본 <span className="text-red-500">*</span>
                  </h3>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange('idCopyFile', e.target.files?.[0] || null)}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                      required
                    />
                    {formData.idCopyFile && (
                      <span className="text-green-600 whitespace-nowrap">✓ 업로드됨</span>
                    )}
                  </div>
                </div>

                {/* 환급 통장 사본 */}
                <div>
                  <h3 className="text-slate-800 text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                    환급 통장 사본 <span className="text-red-500">*</span>
                  </h3>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange('bankbookCopyFile', e.target.files?.[0] || null)}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                      required
                    />
                    {formData.bankbookCopyFile && (
                      <span className="text-green-600 whitespace-nowrap">✓ 업로드됨</span>
                    )}
                  </div>
                </div>

                {/* 증빙서류 (인사변경시 필수) */}
                {formData.moveOutReason === 'personnel_change' && (
                  <div>
                    <h3 className="text-slate-800 text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                      증빙서류 (퇴직증명서, 발령장 등) <span className="text-red-500">*</span>
                    </h3>
                    <div className="flex items-center space-x-4">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange('proofDocumentFile', e.target.files?.[0] || null)}
                        className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-md file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100"
                        required
                      />
                      {formData.proofDocumentFile && (
                        <span className="text-green-600 whitespace-nowrap">✓ 업로드됨</span>
                      )}
                    </div>
                    <div className="mt-4 text-red-600 text-sm">
                      * 인사변경 사유 선택시 증빙서류를 반드시 첨부하세요
                    </div>
                  </div>
                )}

                {submitting && (
                  <div className="mt-4 text-center text-gray-600">
                    파일 업로드 중...
                  </div>
                )}
              </div>
            </div>
          </form>

          {/* 하단 여백 */}
          <div className="h-32"></div>

          {/* 하단 버튼 섹션 */}
          <div className="fixed bottom-0 left-0 w-full h-24 bg-white border-t border-gray-200">
            <div className="max-w-[1600px] mx-auto px-4 h-full flex items-center justify-end">
              <button
                type="button"
                onClick={() => navigate('/portville/residency')}
                className="w-44 h-16 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-50 mr-4"
              >
                <div className="text-center text-gray-700 text-lg font-bold font-['Pretendard'] leading-9">
                  취소
                </div>
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`w-44 h-16 ${
                  !submitting
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gray-400 cursor-not-allowed'
                } rounded-sm`}
              >
                <div className="text-center text-white text-lg font-bold font-['Pretendard'] leading-9">
                  {submitting ? '제출 중...' : '퇴거 신청하기'}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default MoveOutRequestPage; 
